import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

/**
 * Handles Supabase password-recovery links.
 *
 * A recovery link does NOT set a password — it signs the user in and expects the
 * app to present a "choose a new password" form. Without this page the link just
 * drops the user on the dashboard, they assume the reset worked, and the password
 * is unchanged — leaving them locked out with no way back.
 *
 * This route must sit OUTSIDE ProtectedLayout: the recovery session already counts
 * as signed in, so a redirect-if-authenticated guard would bounce the user away
 * before they can set a password.
 */
export const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [ready, setReady]       = useState(false);
  const [invalid, setInvalid]   = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase parses the token from the URL and emits PASSWORD_RECOVERY.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setReady(true);
    });

    // Arriving with a session already established (link handled before we mounted).
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
      else setTimeout(() => setInvalid(prev => (prev ? prev : !session)), 2500);
    });

    return () => subscription.unsubscribe();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);

    if (error) { toast.error(error.message); return; }
    toast.success('Password updated — please sign in');
    // Sign out so the new password is actually used, rather than riding the
    // recovery session and never confirming it works.
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-title">Set a new password</div>
        <div className="login-subtitle">Choose a password for your Car Collector Studio account</div>

        {invalid && !ready ? (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, color: '#f97316', marginBottom: 16, lineHeight: 1.6 }}>
              This reset link is invalid or has expired. Request a new one from the sign-in page.
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => navigate('/login')}>Back to sign in</button>
          </div>
        ) : !ready ? (
          <div style={{ marginTop: 24, fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>
            Checking your reset link…
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
            <div className="form-group">
              <label className="form-label">New password</label>
              <input className="form-input" type="password" value={password} autoFocus
                onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" required />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm new password</label>
              <input className="form-input" type="password" value={confirm}
                onChange={e => setConfirm(e.target.value)} placeholder="Repeat it" required />
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving}
              style={{ width: '100%', justifyContent: 'center', padding: '11px' }}>
              {saving ? 'Saving…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};